'use client';

import { useState, useCallback, useEffect, useRef } from 'react';
import { orderApi } from '@/entities/order';
import type { OrderSearchView } from '@/entities/order';
import { createRealtimeOrderQuery } from '@/widgets/order/order-list-table/model/search';

const POLL_INTERVAL_MS = 5000;

export const LIVE_PARAM_KEYS = {
  liveMode: 'liveMode',
  liveSince: 'liveSince',
  liveCursor: 'liveCursor',
} as const;

export interface LiveOrderSearchParams {
  liveMode?: string;
  liveSince?: string;
  liveCursor?: string | null;
}

export interface UseLiveOrderSearchOptions {
  /** URL/탭 params에서 복원할 초기값 */
  initialParams?: LiveOrderSearchParams;
  /** params 변경 시 호출 (URL/탭 동기화용) */
  onParamsChange?: (params: Partial<LiveOrderSearchParams>) => void;
}

export function useLiveOrderSearch(options: UseLiveOrderSearchOptions = {}) {
  const { initialParams, onParamsChange } = options;
  const [data, setData] = useState<OrderSearchView | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [isActive, setIsActive] = useState(false);
  const startTimeRef = useRef<Date | null>(null);
  const cursorRef = useRef<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const onParamsChangeRef = useRef(onParamsChange);
  onParamsChangeRef.current = onParamsChange;

  const fetchRealtimeOrders = useCallback(async (useCursor: boolean = false) => {
    const startTime = startTimeRef.current;
    if (!startTime) return;

    const cursor = useCursor ? cursorRef.current : null;
    setLoading(true);
    setError(null);
    try {
      const query = createRealtimeOrderQuery(startTime, cursor);
      const result = await orderApi.searchOrders(query);
      setData(result);
      if (result.nextCursor) {
        cursorRef.current = result.nextCursor;
        onParamsChangeRef.current?.({ [LIVE_PARAM_KEYS.liveCursor]: result.nextCursor });
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('실시간 주문 조회에 실패했습니다.'));
    } finally {
      setLoading(false);
    }
  }, []);

  const start = useCallback(
    (startTime?: Date) => {
      const since = startTime ?? new Date();
      startTimeRef.current = since;
      cursorRef.current = initialParams?.liveCursor ?? null;
      setIsActive(true);

      const params: LiveOrderSearchParams = {
        [LIVE_PARAM_KEYS.liveMode]: '1',
        [LIVE_PARAM_KEYS.liveSince]: String(since.getTime()),
      };
      if (cursorRef.current) params[LIVE_PARAM_KEYS.liveCursor] = cursorRef.current;
      onParamsChangeRef.current?.(params);

      fetchRealtimeOrders(!!cursorRef.current);
      intervalRef.current = setInterval(() => fetchRealtimeOrders(false), POLL_INTERVAL_MS);
    },
    [initialParams?.liveCursor, fetchRealtimeOrders]
  );

  const stop = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    startTimeRef.current = null;
    cursorRef.current = null;
    setIsActive(false);
    setData(null);
    setError(null);
    onParamsChangeRef.current?.({
      [LIVE_PARAM_KEYS.liveMode]: undefined,
      [LIVE_PARAM_KEYS.liveSince]: undefined,
      [LIVE_PARAM_KEYS.liveCursor]: undefined,
    });
  }, []);

  // URL/탭 params에서 복원 (마운트 시 1회)
  // liveSince: 숫자(Unix ms) 또는 ISO 문자열 지원
  const hasRestoredRef = useRef(false);
  useEffect(() => {
    if (hasRestoredRef.current) return;
    if (!initialParams?.liveMode || !initialParams?.liveSince) return;

    const raw = String(initialParams.liveSince);
    const ms = /^\d+$/.test(raw) ? Number(raw) : Date.parse(raw);
    const since = new Date(ms);
    if (isNaN(since.getTime())) return;

    hasRestoredRef.current = true;
    start(since);
  }, [initialParams?.liveMode, initialParams?.liveSince, start]);

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return {
    data,
    loading,
    error,
    isActive,
    start,
    stop,
  };
}
